import React from 'react';
import moment from 'moment-timezone';
import DATE_FORMAT from '../components/constants/dateFormat';
import ITEM_MEASUREMENT_UNIT from '../components/constants/itemMeasurementUnit';

const getErrorMessageFromResponse = function (error, returnAsString) {
  if (error.response && error.response.data && error.response.data.errors) {
    if (returnAsString) {
      return error.response.data.errors.join('; ');
    } else {
      return error.response.data.errors.map(function (message, index) {
        return (
          <span key={index}>
            {message}
            <br />
          </span>
        );
      });
    }
  } else {
    if (returnAsString) {
      return 'Something went wrong. Please refresh and try again.';
    } else {
      return (
        <span>
          Something went wrong. Please refresh and try again.
          <br />
        </span>
      );
    }
  }
};

const getUserLocale = function () {
  return (
    window.userLanguage ||
    (navigator.languages && navigator.languages[0]) ||
    navigator.language ||
    navigator.userLanguage
  );
};

const isNormalInteger = (str) => {
  return /^\+?(0|[1-9]\d*)$/.test(str);
};

const userIsAdmin = (currentUser) => {
  return currentUser && currentUser.authorities.indexOf('ROLE_ADMIN') !== -1;
};

const isFunction = (functionToCheck) => {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
};

const isValidUrl = (str) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ); // fragment locator
  return !!pattern.test(str);
};

const getDateFromString = (stringParam, format = DATE_FORMAT.dataTransfer) => {
  if (stringParam) {
    let date;
    if (format) {
      date = moment.utc(stringParam, format).tz(moment.tz.guess());
    } else {
      date = moment.utc(stringParam).tz(moment.tz.guess());
    }
    if (date && date.isValid()) {
      return date;
    }
  }

  return null;
};

const getNowDate = () => {
  return moment();
};

const getFormattedDateFromString = (stringParam, format) => {
  const date = getDateFromString(stringParam);
  if (!date) {
    return '';
  }

  return date.format(format);
};

const getPdfCopy = (intl, bill) => {
  return {
    from: {
      companyName: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.company.name' }),
      registryCode: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.registry.code' }),
      address: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.address' }),
      vatNumber: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.vat.number' }),
      fullName: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.full.name' }),
      iban: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.iban' }),
    },
    to: {
      companyName: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.to.company.name' }),
      fullName: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.to.full.name' }),
      registryCode: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.to.registry.code' }),
      address: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.to.address' }),
      vatNumber: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.to.vat.number' }),
    },
    body: {
      header: intl.formatMessage(
        { id: 'marketplace.admin.delivery.day.bill.header' },
        {
          billId: bill.title,
        }
      ),
      dateCreated: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.date.created' }),
      deadline: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.deadline' }),
    },
    table: {
      product: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.table.header.product' }),
      quantity: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.table.header.quantity' }),
      totalWithoutVat: intl.formatMessage({
        id: 'marketplace.admin.delivery.day.bill.table.header.total.price.without.vat',
      }),
      total: intl.formatMessage({
        id:
          bill.from && bill.from.vatNumber
            ? 'marketplace.admin.delivery.day.bill.table.header.total.price.with.vat'
            : 'marketplace.admin.delivery.day.bill.table.header.total.price',
      }),
    },
    total: {
      withoutVat: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.table.total.without.vat' }),
      vat: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.table.vat' }),
      total: intl.formatMessage({
        id:
          bill.from && bill.from.vatNumber
            ? 'marketplace.admin.delivery.day.bill.table.total.with.vat'
            : 'marketplace.admin.delivery.day.bill.table.total',
      }),
    },
    contact: {
      email: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.email' }),
      phoneNumber: intl.formatMessage({ id: 'marketplace.admin.delivery.day.bill.from.phone.number' }),
    },
  };
};

const getPayoutReportPdfCopy = (intl, payout) => {
  return {
    info: {
      recipientName: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.to.recipient.name' }),
      iban: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.to.iban' }),
      orderCount: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.order.count' }),
    },
    body: {
      header: intl.formatMessage(
        { id: 'marketplace.admin.payout.pdf.header' },
        {
          payoutId: payout.id,
        }
      ),
      dateCreated: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.date.created' }),
    },
    table: {
      orderId: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.table.header.order.id' }),
      name: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.table.header.name' }),
      items: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.table.header.items' }),
      totalWithoutVat: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.table.header.total.price.without.vat' }),
      total: intl.formatMessage({
        id: payout.vatNumber
          ? 'marketplace.admin.payout.pdf.table.header.total.price.with.vat'
          : 'marketplace.admin.payout.pdf.table.header.total.price',
      }),
    },
    total: {
      vatBeforeDeductions: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.vat.before.deductions' }),
      amountBeforeDeductions: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.amount.before.deductions' }),
      amountBeforeDeductionsWithoutVat: intl.formatMessage({
        id: 'marketplace.admin.payout.pdf.amount.before.deductions.without.vat',
      }),
      commission: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.commission' }),
      total: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.amount' }),
    },
    contact: {
      email: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.from.email' }),
      phoneNumber: intl.formatMessage({ id: 'marketplace.admin.payout.pdf.from.phone.number' }),
    },
  };
};

const getOrderBatchBillPdfCopy = (intl) => {
  return {
    order: {
      title: intl.formatMessage({ id: 'order.batch.bill.pdf.order.titl' + 'e' }),
    },
    from: {
      companyName: intl.formatMessage({ id: 'order.batch.bill.pdf.from.company.name' }),
      buyer: intl.formatMessage({ id: 'order.batch.bill.pdf.from.buyer' }),
      buyerValue: intl.formatMessage({ id: 'order.batch.bill.pdf.from.buyer.value' }),
      registryCode: intl.formatMessage({ id: 'order.batch.bill.pdf.from.registry.code' }),
      vatNumber: intl.formatMessage({ id: 'order.batch.bill.pdf.from.vat.number' }),
      address: intl.formatMessage({ id: 'order.batch.bill.pdf.from.address' }),
      date: intl.formatMessage({ id: 'order.batch.bill.pdf.from.date' }),
    },
    table: {
      product: intl.formatMessage({ id: 'order.batch.bill.pdf.table.header.product' }),
      quantity: intl.formatMessage({ id: 'order.batch.bill.pdf.table.header.quantity' }),
      total: intl.formatMessage({ id: 'order.batch.bill.pdf.table.header.total' }),
      totalWithVat: intl.formatMessage({ id: 'order.batch.bill.pdf.table.header.total.with.vat' }),
      delivery: intl.formatMessage({ id: 'order.batch.bill.pdf.table.delivery.cost' }),
    },
    total: {
      total: intl.formatMessage({ id: 'order.batch.bill.pdf.total.total' }),
      totalWithVat: intl.formatMessage({ id: 'order.batch.bill.pdf.total.total.with.vat' }),
      totalWithoutVat: intl.formatMessage({ id: 'order.batch.bill.pdf.total.total.without.vat' }),
    },
    combined: {
      header: intl.formatMessage({ id: 'order.batch.bill.pdf.combined.header' }),
      coupon: intl.formatMessage({ id: 'order.batch.bill.pdf.combined.coupon' }),
      refund: intl.formatMessage({ id: 'order.batch.bill.pdf.combined.refund' }),
      order: intl.formatMessage({ id: 'order.batch.bill.pdf.combined.order' }),
    },
  };
};

const getCourierPdfCopy = (intl) => {
  return {
    info: {
      fruits: intl.formatMessage({ id: 'courier.pdf.info.fruits' }),
      vegetables: intl.formatMessage({ id: 'courier.pdf.info.vegs' }),
    },
    table: {
      boxNumber: intl.formatMessage({ id: 'courier.pdf.table.box.number' }),
      clientId: intl.formatMessage({ id: 'courier.pdf.table.client.id' }),
      boxContent: intl.formatMessage({ id: 'courier.pdf.table.box.content' }),
    },
  };
};

const getPackerDeliveryDayMarketplacePdfCopy = (intl) => {
  return {
    table: {
      itemName: intl.formatMessage({ id: 'packer.ddm.pdf.table.item.name' }),
      quantity: intl.formatMessage({ id: 'packer.ddm.pdf.table.quantity' }),
      status: intl.formatMessage({ id: 'packer.ddm.pdf.table.status' }),
    },
  };
};

const getRoundedPrice = (price, multiplier = 100) => {
  return Math.round((price + Number.EPSILON) * multiplier) / multiplier;
};

const groupBy = function (array, key) {
  return array.reduce(function (result, elem) {
    (result[elem[key]] = result[elem[key]] || []).push(elem);
    return result;
  }, {});
};

const groupByValueFunc = function (array, valueFunction) {
  return array.reduce(function (result, elem) {
    const value = valueFunction(elem);

    (result[value] = result[value] || []).push(elem);
    return result;
  }, {});
};

const getFormattedTimeRangeFromDateStrings = ({ from, to }) => {
  return `${getFormattedDateFromString(from, DATE_FORMAT.onlyTime)} - ${getFormattedDateFromString(
    to,
    DATE_FORMAT.onlyTime
  )}`;
};

const getFormattedTimeRange = (timeRange) => {
  return `${timeRange.isBusinessToBusiness ? 'B2B ' : ''}${getFormattedTimeRangeFromHourAndMinutes(
    timeRange.fromHour,
    timeRange.fromMinutes,
    timeRange.toHour,
    timeRange.toMinutes
  )}`;
};

const getFormattedTimeRangeFromHourAndMinutes = (fromHour, fromMinutes, toHour, toMinutes) => {
  return `${fromHour}:${fromMinutes < 10 ? '0' : ''}${fromMinutes} - ${toHour}:${
    toMinutes < 10 ? '0' : ''
  }${toMinutes}`;
};

const getFormattedDateFromMomentObj = (date, format) => {
  return date.format(format);
};

const getFormattedDateRangeFromStrings = (dateFrom, dateTo, showDate) => {
  const from = getDateFromString(dateFrom);
  const to = getDateFromString(dateTo);
  if (!from || !to) {
    return '';
  }

  return `${
    showDate ? `${getFormattedDateFromMomentObj(from, DATE_FORMAT.onlyDate)} - ` : ''
  }${getFormattedDateFromMomentObj(from, DATE_FORMAT.onlyTime)} - ${getFormattedDateFromMomentObj(
    to,
    DATE_FORMAT.onlyTime
  )}`;
};

const isNumeric = (input) => {
  const regex = /^-{0,1}\d*\.{0,1}\d+$/;
  return regex.test(input);
};

const getDeliveryDayForForm = (deliveryDay) => {
  const serviceAreaIds = deliveryDay.serviceAreas.map((sa) => sa.id);
  const result = { ...deliveryDay };
  result.serviceAreas = serviceAreaIds;
  result.timeRanges = deliveryDay.timeRanges.map((tr) => ({
    id: tr.id,
    fromHour: `${tr.fromHour}`,
    fromMinutes: `${tr.fromMinutes}`,
    toHour: `${tr.toHour}`,
    toMinutes: `${tr.toMinutes}`,
    serviceAreaName: `${tr.serviceAreaName}`,
    isBusinessToBusiness: tr.isBusinessToBusiness,
    remaining: tr.remaining || tr.remaining === 0 ? `${tr.remaining}` : '',
  }));
  return result;
};

const getRoutePlanSetupForForm = (rps) => {
  return {
    ...rps,
    deliveryDayTimeRanges: rps.deliveryDayTimeRanges.map((ddtr) => ddtr.id),
    pickupSlots: rps.pickupSlots.map((ps) => {
      const pickupFrom = getDateFromString(ps.pickupFrom);
      const pickupTo = getDateFromString(ps.pickupTo);
      return {
        ...ps,
        startDepotId: ps.startDepot?.id,
        pickupFromHour: pickupFrom.format('H'),
        pickupFromMinutes: pickupFrom.format('m'),
        pickupToHour: pickupTo.format('H'),
        pickupToMinutes: pickupTo.format('m'),
        vehicles: ps.vehicles.map((vehicle) => ({
          ...vehicle,
          count: vehicle.count.toString(),
          capacity: vehicle.capacity.toString(),
          maxTravelTimeInMinutes: vehicle.maxTravelTimeInMinutes ? vehicle.maxTravelTimeInMinutes.toString() : '',
          maxLateTimeInMinutes: vehicle.maxLateTimeInMinutes ? vehicle.maxLateTimeInMinutes.toString() : '',
        })),
      };
    }),
  };
};

const getItemUnitQuantity = (item, formatMessage) => {
  const measurementUnitKey = item.measurementUnit
    ? Object.keys(ITEM_MEASUREMENT_UNIT).find((key) => ITEM_MEASUREMENT_UNIT[key].name === item.measurementUnit)
    : null;

  if (measurementUnitKey) {
    return `, ${item.standardUnitQuantity} ${formatMessage({
      id: ITEM_MEASUREMENT_UNIT[measurementUnitKey].shorthandTranslation,
    })}`;
  } else {
    return '';
  }
};

const getItemName = (item, formatMessage) => {
  const measurementUnitKey = item.measurementUnit
    ? Object.keys(ITEM_MEASUREMENT_UNIT).find((key) => ITEM_MEASUREMENT_UNIT[key].name === item.measurementUnit)
    : null;

  if (measurementUnitKey && item.standardUnitQuantity) {
    return `${item.name}, ${item.standardUnitQuantity} ${formatMessage({
      id: ITEM_MEASUREMENT_UNIT[measurementUnitKey].shorthandTranslation,
    })}`;
  } else {
    return item.name;
  }
};

const utils = {
  getErrorMessageFromResponse,
  getUserLocale,
  isNormalInteger,
  userIsAdmin,
  isFunction,
  isValidUrl,
  getDateFromString,
  getNowDate,
  getFormattedDateFromString,
  getPdfCopy,
  getPayoutReportPdfCopy,
  getRoundedPrice,
  groupBy,
  groupByValueFunc,
  getFormattedTimeRange,
  getFormattedTimeRangeFromHourAndMinutes,
  getFormattedDateRangeFromStrings,
  getFormattedTimeRangeFromDateStrings,
  getOrderBatchBillPdfCopy,
  getCourierPdfCopy,
  getPackerDeliveryDayMarketplacePdfCopy,
  isNumeric,
  getDeliveryDayForForm,
  getRoutePlanSetupForForm,
  getItemName,
  getItemUnitQuantity,
};

export default utils;
